import React, { useRef, useState } from "react";
import { FieldValues, Path, useController } from "react-hook-form";
import { Select, Skeleton } from "antd5";

import Spinner from "components/ui/spinner/Spinner";
import { useDebouncedValue } from "lib/debounce";
import { useOrganisations } from "lib/hooks/api/organisations/useOrganisations";
import { useSignalSettingCountryCodes } from "lib/hooks/api/teams/useSignalSettings";
import {
  ExportOrgSortBy as BuyerSearchSortBy,
  ExportOrgSortOrder as BuyerSearchSortOrder,
  SearchOrgRole,
} from "lib/types/graphQLEnums";
import { SelectProps } from "./Inputs";

import css from "./Inputs.module.scss";

const ADD_ALL_VALUE = "ALL";

type FieldProps<T extends FieldValues> = Omit<SelectProps<T>, "options" | "name"> & {
  buyersFieldName: Path<T>;
  customBuyersFieldName: Path<T>;
};

export function BuyerSelectWithCustom<T extends FieldValues>({
  buyersFieldName,
  customBuyersFieldName,
  control,
  mode,
  placeholder,
  disabled,
  allowClear,
}: FieldProps<T>) {
  const { field: buyersField } = useController({ name: buyersFieldName, control });
  const { field: customBuyersField } = useController({ name: customBuyersFieldName, control });

  const [searchText, setSearchText] = useState<string>("");
  const [debouncedText] = useDebouncedValue(searchText, 300);

  const userCountryCodes = useSignalSettingCountryCodes();

  // Combined selected IDs for display (stotles + custom)
  const allSelected: string[] = [...(buyersField.value ?? []), ...(customBuyersField.value ?? [])];

  // Track which IDs belong to custom orgs so we can split on change
  const customOrgIds = useRef<Set<string>>(new Set());

  const fetchSelectedBuyers = allSelected.length > 0 && !debouncedText;

  const { data: selectedBuyers, isLoading: isLoadingSelected } = useOrganisations(
    {
      limit: allSelected.length,
      page: 1,
      // No countryCodes: an already-selected buyer must resolve its name even when it sits
      // outside the user's feed countries.
      ids: allSelected,
      role: SearchOrgRole.Buyer,
      sortBy: BuyerSearchSortBy.NoticeCount,
      sortOrder: BuyerSearchSortOrder.Desc,
      includeCustomOrganisations: true,
    },
    {
      enabled: fetchSelectedBuyers,
      staleTime: Infinity,
      cacheTime: Infinity,
    },
  );

  const { data: dropdownBuyers, isLoading } = useOrganisations(
    {
      sortBy: BuyerSearchSortBy.NoticeCount,
      sortOrder: BuyerSearchSortOrder.Desc,
      role: SearchOrgRole.Buyer,
      limit: 20,
      page: 1,
      names: debouncedText ? [debouncedText] : undefined,
      countryCodes: userCountryCodes,
      includeCustomOrganisations: true,
    },
    { enabled: !fetchSelectedBuyers && debouncedText.length > 1 },
  );

  let options: { label: string; value: string; isCustom?: boolean | null }[] = dropdownBuyers
    ?.searchOrganisations.orgs
    ? dropdownBuyers.searchOrganisations.orgs.map((buyer) => ({
        label: buyer.name,
        value: buyer.id,
        isCustom: buyer.isCustom,
      }))
    : [];

  if (!debouncedText && selectedBuyers?.searchOrganisations.orgs && options.length === 0) {
    options = selectedBuyers.searchOrganisations.orgs.map((buyer) => ({
      label: buyer.name,
      value: buyer.id,
      isCustom: buyer.isCustom,
    }));
  }

  // Keep the customOrgIds ref in sync with loaded options
  const allLoadedOrgs = [
    ...(dropdownBuyers?.searchOrganisations.orgs ?? []),
    ...(selectedBuyers?.searchOrganisations.orgs ?? []),
  ];
  for (const org of allLoadedOrgs) {
    if (org.isCustom) {
      customOrgIds.current.add(org.id);
    }
  }

  // Prepend "Add N matching" option when searching and not all are already selected
  const showAddAll =
    !!debouncedText &&
    options.length > 0 &&
    !options.every((opt) => allSelected.includes(opt.value));

  const displayOptions = showAddAll
    ? [
        {
          label: <span className={css.addAllSelectOption}>Add {options.length} matching</span>,
          value: ADD_ALL_VALUE,
        },
        ...options,
      ]
    : options;

  const handleChange = (value: string[]) => {
    let ids = value;
    if (ids.includes(ADD_ALL_VALUE)) {
      ids = ids.filter((id) => id !== ADD_ALL_VALUE);
      const optionValues = options.map((opt) => opt.value);
      ids = [...new Set([...ids, ...optionValues])];
    }
    const stotlesIds = ids.filter((id) => !customOrgIds.current.has(id));
    const customIds = ids.filter((id) => customOrgIds.current.has(id));
    buyersField.onChange(stotlesIds);
    customBuyersField.onChange(customIds);
  };

  const loading =
    (isLoading && !fetchSelectedBuyers && !dropdownBuyers) ||
    (isLoadingSelected && fetchSelectedBuyers && !selectedBuyers);

  const renderDropdown = (menu: React.ReactElement) => {
    if (loading && !!debouncedText) {
      return <Skeleton style={{ padding: "8px", paddingBottom: "0px" }} active />;
    }
    return menu;
  };

  const dropdownStyle = debouncedText === "" ? { display: "none" } : {};

  return (
    <Select
      id={buyersFieldName}
      mode={mode ?? "multiple"}
      placeholder={placeholder}
      disabled={disabled}
      allowClear={allowClear}
      value={allSelected}
      onChange={handleChange}
      options={displayOptions}
      optionFilterProp="label"
      labelRender={({ label: lbl, value }) =>
        lbl == null || lbl.toString() === value?.toString() ? (
          <Spinner color="currentColor" label="Loading buyer" />
        ) : (
          lbl
        )
      }
      dropdownRender={renderDropdown}
      dropdownStyle={dropdownStyle}
      onSearch={(val) => setSearchText(val)}
      showSearch
      filterOption={false}
      loading={loading && !!debouncedText}
    />
  );
}
